import React, { useState, useEffect } from 'react';
import { X, Printer, Loader2, CheckCircle, AlertCircle, Tag } from 'lucide-react';
import { printingService, LabelType } from '../../../../../services/printingService';
import type { Printer as PrinterType } from '../../../../../types/printNode';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { FreeTextFieldsModal, extractFreeTextFields } from '../../../../common/FreeTextFieldsModal';
import { MAX_LABELS_PER_PRINT_JOB } from '../../../../../constants/labelPrinting';

// Diverse/generisk etikett (ReportType 60) — custom LabelDesigner-mal med valgfri fritekst
const isDiverseReportType = (reportType: number): boolean => reportType === 60;

interface ReceiptPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyNo: string;
  // Pre-filled data from received item
  itemNo?: string;
  itemDescription?: string;
  batch?: string;
  quantity?: number;
}

export const ReceiptPrintModal: React.FC<ReceiptPrintModalProps> = ({
  isOpen,
  onClose,
  companyNo,
  itemNo: initialItemNo = '',
  itemDescription: initialItemDescription = '',
  batch: initialBatch = '',
  quantity: initialQuantity
}) => {
  const { t } = useTranslation();
  const [printers, setPrinters] = useState<PrinterType[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPrinters, setLoadingPrinters] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [copies, setCopies] = useState(1);

  // Label type selection
  const [labelTypes, setLabelTypes] = useState<LabelType[]>([]);
  const [selectedLabelType, setSelectedLabelType] = useState<number | null>(null);
  const [loadingLabelTypes, setLoadingLabelTypes] = useState(true);

  // Item label fields - pre-filled from props
  const [itemNo, setItemNo] = useState(initialItemNo);
  const [itemDescription, setItemDescription] = useState(initialItemDescription);
  const [itemBatch, setItemBatch] = useState(initialBatch);
  const [itemQuantity, setItemQuantity] = useState<number | undefined>(initialQuantity);

  // Fritekst for Diverse-etiketter (mal-feltet "FreeText")
  const [freeText, setFreeText] = useState('');

  // Frifelt-modal (FreeText1-5): åpnes før utskrift når valgt mal bruker frifelt
  const [freeTextModalOpen, setFreeTextModalOpen] = useState(false);

  const selectedLabelTypeObj = labelTypes.find((lt) => lt.reportID === selectedLabelType);

  // Er valgt rapport av typen Diverse (60)? Da printes generisk mal i stedet for vareetikett
  const selectedIsDiverse = isDiverseReportType(selectedLabelTypeObj?.reportType ?? 0);

  // Frifelt (FreeText1-5) som valgt mal bruker — utløser frifelt-modalen før utskrift
  const freeTextFields = extractFreeTextFields(selectedLabelTypeObj?.layoutJson);

  // Load printers and label types when modal opens
  useEffect(() => {
    if (isOpen && companyNo) {
      loadPrinters();
      loadLabelTypes();
      resetForm();
    }
  }, [isOpen, companyNo]);

  // Update form fields when props change
  useEffect(() => {
    if (isOpen) {
      setItemNo(initialItemNo);
      setItemDescription(initialItemDescription);
      setItemBatch(initialBatch);
      setItemQuantity(initialQuantity);
    }
  }, [isOpen, initialItemNo, initialItemDescription, initialBatch, initialQuantity]);

  // Load item default report when itemNo changes
  useEffect(() => {
    if (isOpen && companyNo && itemNo) {
      loadItemDefaultReport();
    }
  }, [isOpen, companyNo, itemNo]);

  const resetForm = () => {
    setItemNo(initialItemNo);
    setItemDescription(initialItemDescription);
    setItemBatch(initialBatch);
    setItemQuantity(initialQuantity);
    setFreeText('');
    setError(null);
    setSuccess(false);
    setCopies(1);
  };

  const loadPrinters = async () => {
    if (!companyNo) {
      setError(t('warehouse.modules.receipt.print.companyNoMissing'));
      setLoadingPrinters(false);
      return;
    }

    setLoadingPrinters(true);
    setError(null);
    try {
      const printersData = await printingService.getAvailablePrinters(companyNo);
      setPrinters(printersData);

      // Auto-select first printer if available
      if (printersData.length > 0) {
        setSelectedPrinterId(printersData[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load printers:', err);
      setError(t('warehouse.modules.receipt.print.loadPrintersFailed', { 0: err.message }));
    } finally {
      setLoadingPrinters(false);
    }
  };

  const loadLabelTypes = async () => {
    if (!companyNo) return;

    setLoadingLabelTypes(true);
    try {
      const types = await printingService.getLabelTypesForReceiving(companyNo);
      setLabelTypes(types);

      // Auto-select first label type if none selected. Funksjonell oppdatering:
      // loadItemDefaultReport kjører parallelt og varens Items.ReportID skal trumfe
      // førstevalget — en vanlig closure her leser stale null og overskriver den (TW 36386410).
      if (types.length > 0) {
        setSelectedLabelType(prev => prev ?? types[0].reportID);
      }
    } catch (error) {
      console.error('Error loading label types:', error);
    } finally {
      setLoadingLabelTypes(false);
    }
  };

  const loadItemDefaultReport = async () => {
    if (!companyNo || !itemNo) return;

    try {
      const reportId = await printingService.getItemDefaultReport(companyNo, itemNo);
      if (reportId && reportId > 0) {
        setSelectedLabelType(reportId);
      }
    } catch (error) {
      console.error('Error loading item default report:', error);
    }
  };

  const handlePrint = async () => {
    if (!selectedPrinterId) {
      setError(t('warehouse.modules.receipt.print.selectPrinterError'));
      return;
    }

    // Validate required fields (gjelder kun vareetiketter — Diverse trenger ingen varedata)
    if (!selectedIsDiverse && (!itemNo || !itemDescription)) {
      setError(t('warehouse.modules.receipt.print.fillItemAndDescription'));
      return;
    }

    // Malen har frifelt → operatøren må fylle inn og bekrefte i frifelt-modalen først
    if (freeTextFields.length > 0) {
      setFreeTextModalOpen(true);
      return;
    }

    await executePrint(undefined);
  };

  /**
   * Kjøres fra frifelt-modalen. Modalen forblir åpen — resultatet returneres og vises
   * der, slik at operatøren kan justere teksten og skrive ut på nytt.
   * `printerIdOverride` er skrivervalget i modalen (null = skriveren valgt i denne dialogen).
   */
  const handleFreeTextConfirm = async (values: Record<string, string>, printerIdOverride: number | null) => {
    return await executePrint(values, printerIdOverride);
  };

  const executePrint = async (
    freeTextVals?: Record<string, string>,
    printerOverride?: number | null
  ): Promise<{ success: boolean; message?: string }> => {
    // Utskrift fra frifelt-modalen: ikke auto-lukk hoveddialogen — operatøren
    // står fortsatt i frifelt-modalen og styrer selv når den lukkes.
    const fromFreeTextModal = freeTextVals !== undefined;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const effectivePrinterId = printerOverride ?? selectedPrinterId;
      const result = selectedIsDiverse
        ? await printingService.printGenericLabel(companyNo, {
            reportId: selectedLabelType,
            numOfCopies: copies,
            freeText: freeText || undefined,
            printerIdOverride: effectivePrinterId,
            freeTextValues: freeTextVals
          })
        : await printingService.printLabel(companyNo, {
            reportID: selectedLabelType,
            numOfLabels: copies,
            itemNo: itemNo,
            batchNo: itemBatch || null,
            quantity: itemQuantity || null,
            printerIdOverride: effectivePrinterId,
            freeTextValues: freeTextVals
          });

      if (result.success) {
        setSuccess(true);
        if (!fromFreeTextModal) {
          setTimeout(() => {
            onClose();
            resetForm();
          }, 2000);
        }
      } else {
        setError(result.message);
      }
      return { success: result.success, message: result.message };
    } catch (err: any) {
      console.error('Print failed:', err);
      setError(t('warehouse.modules.receipt.print.printFailed', { 0: err.message }));
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Printer className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('warehouse.modules.receipt.printItemLabel')}</h2>
              <p className="text-sm text-gray-500">{t('warehouse.modules.receipt.print.labelForReceivedItem')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
            <Tag className="text-blue-600 flex-shrink-0" size={18} />
            <div className="text-sm text-blue-800">
              <strong>{t('warehouse.modules.receipt.print.itemLabel')}:</strong> {t('warehouse.modules.receipt.print.fieldsPrefilled')}
            </div>
          </div>

          {/* Label Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('warehouse.modules.receipt.print.labelType')}
            </label>
            <select
              value={selectedLabelType || ''}
              onChange={(e) => setSelectedLabelType(e.target.value ? parseInt(e.target.value) : null)}
              disabled={loadingLabelTypes}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {loadingLabelTypes ? (
                <option value="">{t('warehouse.modules.receipt.print.loadingLabelTypes')}</option>
              ) : (
                <>
                  <option value="">{t('warehouse.modules.receipt.print.selectLabelType')}</option>
                  {labelTypes.map((type) => (
                    <option key={type.reportID} value={type.reportID}>
                      {type.description}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {/* Fritekst for Diverse-etiketter (custom mal, ingen varedata) */}
          {selectedIsDiverse && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('warehouse.modules.receipt.print.freeText', {}, 'Fritekst (valgfri)')}
              </label>
              <input
                type="text"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder={t('warehouse.modules.receipt.print.freeTextPlaceholder', {}, 'Tekst som skal skrives på etiketten...')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Item Label Fields */}
          {!selectedIsDiverse && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('warehouse.modules.receipt.print.itemLabelSettings')}
            </h3>

            {/* Item Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.fields.itemNumber')} *
              </label>
              <input
                type="text"
                value={itemNo}
                onChange={(e) => setItemNo(e.target.value)}
                placeholder={t('warehouse.modules.receipt.print.itemNoPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Item Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.fields.description')} *
              </label>
              <input
                type="text"
                value={itemDescription}
                onChange={(e) => setItemDescription(e.target.value)}
                placeholder={t('warehouse.modules.receipt.print.descriptionPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Batch and Quantity */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('warehouse.modules.receipt.print.batchOptional')}
                </label>
                <input
                  type="text"
                  value={itemBatch}
                  onChange={(e) => setItemBatch(e.target.value)}
                  placeholder={t('warehouse.modules.receipt.print.batchPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('warehouse.modules.receipt.print.quantityOptional')}
                </label>
                <input
                  type="number"
                  value={itemQuantity || ''}
                  onChange={(e) => setItemQuantity(e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
          )}

          {/* Printer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('warehouse.modules.receipt.print.selectPrinter')}
            </label>
            {loadingPrinters ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-blue-600" size={32} />
              </div>
            ) : printers.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">{t('warehouse.modules.receipt.print.noPrintersFound')}</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      {t('warehouse.modules.receipt.print.noPrintersHint')}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {printers.map((printer) => (
                  <label
                    key={printer.id}
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedPrinterId === printer.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="printer"
                      value={printer.id}
                      checked={selectedPrinterId === printer.id}
                      onChange={() => setSelectedPrinterId(printer.id)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <Printer size={18} className="text-gray-600" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{printer.displayName || printer.name}</div>
                      {printer.description && (
                        <div className="text-sm text-gray-500">{printer.description}</div>
                      )}
                    </div>
                    {printer.state === 'online' && (
                      <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded">
                        {t('warehouse.modules.receipt.print.available')}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Copies */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('warehouse.modules.receipt.print.numberOfCopies')}
            </label>
            <input
              type="number"
              min="1"
              max={MAX_LABELS_PER_PRINT_JOB}
              value={copies}
              onChange={(e) => setCopies(Math.max(1, Math.min(MAX_LABELS_PER_PRINT_JOB, parseInt(e.target.value) || 1)))}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                <p className="text-sm text-green-800">{t('warehouse.modules.receipt.print.labelSentToPrint')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handlePrint}
            disabled={loading || !selectedPrinterId || loadingPrinters}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={16} />}
            {loading ? t('warehouse.modules.receipt.print.sending') : t('common.buttons.print')}
          </button>
        </div>
      </div>

      {/* Frifelt-modal: operatør fyller inn FreeText1-5 med live forhåndsvisning før utskrift */}
      {selectedLabelTypeObj?.layoutJson && (
        <FreeTextFieldsModal
          isOpen={freeTextModalOpen}
          companyNo={companyNo}
          layoutJson={selectedLabelTypeObj.layoutJson}
          fields={freeTextFields}
          reportDescription={selectedLabelTypeObj.description}
          context={{ itemNo: itemNo || undefined }}
          onCancel={() => setFreeTextModalOpen(false)}
          onConfirm={handleFreeTextConfirm}
        />
      )}
    </div>
  );
};
